import { useEffect, useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { api } from '../lib/spizirnaUi';
import UctenkaHtml from './UctenkaHtml';

/**
 * Správa → Provozovna → Účtenka (dřív Nastavení → Účtenky).
 *
 * Co je na dokladu a kdy se tiskne sám — platí pro všechny pokladny.
 * Kterou tiskárnou a na jak široký papír, si volí každý terminál
 * v Nastavení pokladny. Náhled vpravo je skutečná šablona účtenky
 * (`/api/uctenky/nahled`) na ukázkové objednávce — přesně to, co vyjede
 * z tiskárny, ukáže se na stránce objednávky a stáhne v PDF.
 */
// Název, sídlo, IČO, DIČ, e-mail a telefon prodejce se berou z identity firmy (Správa → Firma).
const TEXTY = [
    { klic: 'provozovna', label: 'Provozovna (když je jinde než sídlo)', placeholder: 'nepovinné' },
    { klic: 'paticka', label: 'Patička', placeholder: 'Děkujeme! www.fofrlama.cz', viceRadku: true },
];

const PREPINACE = [
    {
        klic: 'tisk_rozvozu',
        label: 'Rozvoz tisknout automaticky',
        popis: 'Při dokončení v kuchyni vyjede účtenka sama — jede s taškou a rozvozce podle ní vybírá. Každá jen jednou.',
    },
    {
        klic: 'nabidnout_po_zaplaceni',
        label: 'Po zaplacení se zeptat na doklad',
        popis: 'Osobní odběr a stoly se netisknou samy. Pokladna se po zaplacení zeptá „Vytisknout doklad?“.',
    },
];

export default function UctenkyNastaveni({ isDark, onOtevritFirmu = null, barva = '#3B82F6' }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState('');
    const [ulozeno, setUlozeno] = useState(false);

    useEffect(() => {
        api('/api/uctenky/nastaveni').then(setData).catch(() => setChyba('Nastavení se nepodařilo načíst.'));
    }, []);

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const fieldBg = isDark ? 'rgba(255,255,255,0.05)' : '#fff';

    const uloz = async (zmena) => {
        setChyba('');
        setData(prev => ({ ...prev, ...zmena }));
        try {
            setData(await api('/api/uctenky/nastaveni', 'PUT', zmena));
            setUlozeno(true);
            setTimeout(() => setUlozeno(false), 1500);
        } catch (e) {
            setChyba(e?.error || e?.message || 'Nepodařilo se uložit.');
        }
    };

    if (!data) {
        return chyba
            ? <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>
            : <Loader2 size={18} className="animate-spin" style={{ color: muted }} />;
    }

    const pole = (t) => {
        const props = {
            value: data[t.klic] ?? '',
            placeholder: t.placeholder,
            onChange: e => setData(prev => ({ ...prev, [t.klic]: e.target.value })),
            onBlur: e => uloz({ [t.klic]: e.target.value }),
            className: 'w-full text-sm rounded-lg px-3 py-2',
            style: { background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', resize: 'none' },
        };
        return (
            <label key={t.klic} className={`block ${t.pul ? '' : 'col-span-2'}`}>
                <span className="block text-[11px] font-semibold mb-1" style={{ color: muted }}>{t.label}</span>
                {t.viceRadku ? <textarea rows={2} {...props} /> : <input type="text" {...props} />}
            </label>
        );
    };

    return (
        <div className="space-y-4 max-w-4xl">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Účtenky</h3>
                    <p className="text-xs mt-1" style={{ color: muted }}>
                        Co je na dokladu a kdy se tiskne sám — platí pro všechny pokladny.
                        Tiskárnu a šířku papíru si volí každý terminál v nastavení pokladny (v aplikaci POS).
                    </p>
                </div>
                {ulozeno && (
                    <span className="flex items-center gap-1 text-xs font-semibold flex-shrink-0" style={{ color: '#10b981' }}>
                        <Check size={13} /> Uloženo
                    </span>
                )}
            </div>

            {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}

            <div className="flex flex-wrap gap-6">
                <div className="flex-1 space-y-5" style={{ minWidth: 300 }}>
                    <section>
                        <h4 className="text-xs font-bold uppercase mb-2" style={{ color: muted, letterSpacing: '0.06em' }}>Prodejce</h4>
                        <div className="rounded-lg mb-3" style={{ padding: '10px 12px', border: `1px solid ${border}` }}>
                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', margin: 0 }}>{data.firma}</p>
                            <p className="text-xs" style={{ color: muted, margin: '2px 0 0' }}>
                                {[data.adresa, data.ico && `IČO ${data.ico}`, data.dic && `DIČ ${data.dic}`].filter(Boolean).join(' · ') || 'Chybí sídlo, IČO a DIČ'}
                            </p>
                            <p className="text-[11px]" style={{ color: muted, margin: '6px 0 0' }}>
                                Bere se z identity firmy (Správa → Firma).
                                {onOtevritFirmu && <button type="button" onClick={onOtevritFirmu} className="font-semibold" style={{ marginLeft: 6, color: barva, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Upravit</button>}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {TEXTY.map(pole)}
                        </div>
                        <p className="text-[11px] mt-2" style={{ color: muted }}>
                            Zjednodušený daňový doklad musí nést název prodejce a DIČ. Nad 10 000 Kč je potřeba plný doklad.
                        </p>
                    </section>

                    <section>
                        <h4 className="text-xs font-bold uppercase mb-2" style={{ color: muted, letterSpacing: '0.06em' }}>Kdy tisknout</h4>
                        <div className="space-y-2">
                            {PREPINACE.map(p => (
                                <button type="button" key={p.klic} onClick={() => uloz({ [p.klic]: !data[p.klic] })}
                                    className="w-full text-left flex items-start gap-3 rounded-xl px-4 py-3 cursor-pointer"
                                    style={{ background: 'transparent', border: `1px solid ${border}`, minHeight: 56 }}>
                                    <span aria-hidden="true" style={{ marginTop: 2, width: 34, height: 20, borderRadius: 10, position: 'relative', flexShrink: 0,
                                        background: data[p.klic] ? barva : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') }}>
                                        <span style={{ position: 'absolute', top: 2, left: data[p.klic] ? 16 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff' }} />
                                    </span>
                                    <span>
                                        <span className="block text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{p.label}</span>
                                        <span className="block text-xs mt-0.5" style={{ color: muted }}>{p.popis}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </section>
                </div>

                <Nahled data={data} />
            </div>
        </div>
    );
}

const REZIMY = [
    { id: 'tisk-80', label: '80 mm', sirka: 576 },
    { id: 'tisk-58', label: '58 mm', sirka: 384 },
    { id: 'barevna', label: 'Obrazovka a PDF', sirka: 360 },
];

/** Náhled skutečné šablony — obnoví se po každém uložení nastavení. */
function Nahled({ data }) {
    const [rezim, setRezim] = useState('tisk-80');
    const [html, setHtml] = useState(null);
    const r = REZIMY.find(x => x.id === rezim);

    useEffect(() => {
        let platne = true;
        api(`/api/uctenky/nahled?rezim=${rezim}`).then(d => { if (platne) setHtml(d.html); }).catch(() => {});
        return () => { platne = false; };
    }, [rezim, data]);

    return (
        <div style={{ width: 300, maxWidth: '100%', flexShrink: 0 }}>
            <h4 className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--text-muted)', letterSpacing: '0.06em' }}>Náhled</h4>
            <div className="flex gap-1 mb-3">
                {REZIMY.map(x => (
                    <button key={x.id} type="button" onClick={() => setRezim(x.id)} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg cursor-pointer"
                        style={{
                            border: '1px solid var(--border-default)',
                            background: rezim === x.id ? 'var(--surface-elevated)' : 'transparent',
                            color: rezim === x.id ? 'var(--text-primary)' : 'var(--text-muted)',
                        }}>{x.label}</button>
                ))}
            </div>
            {html
                ? <UctenkaHtml html={html} sirka={r.sirka} zobrazit={rezim === 'tisk-58' ? 220 : 300} />
                : <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Načítám náhled…</div>}
        </div>
    );
}
